export type MeasureText = (text: string) => number;

export interface FittedLabel {
  text: string;
  cut: boolean;
}

const ELLIPSIS = "…";

// Unicode whitespace minus the no-break spaces (U+00A0, U+2007, U+202F), which
// someone typed precisely so the words would stay together.
const BREAK = String.raw`\t\n\v\f\r \u1680\u2000-\u2006\u2008-\u200A\u2028\u2029\u205F\u3000`;
const WORD_END = new RegExp(`[^${BREAK}](?=[${BREAK}])`, "g");
const TRAILING_JOINERS = new RegExp(
  String.raw`[${BREAK}\u00A0\u2007\u202F\-\u2013\u2014&,:;/|]+$`
);

function stripTrailingJoiners(text: string) {
  return text.replace(TRAILING_JOINERS, "");
}

const segmenter =
  typeof Intl !== "undefined" && "Segmenter" in Intl
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : undefined;

function graphemes(text: string): string[] {
  return segmenter
    ? Array.from(segmenter.segment(text), (part) => part.segment)
    : [...text];
}

function characterCut(text: string, maxWidth: number, measure: MeasureText) {
  const parts = graphemes(text);
  const candidate = (count: number) => {
    const prefix = parts.slice(0, count).join("");
    return (stripTrailingJoiners(prefix) || prefix) + ELLIPSIS;
  };
  let low = 1;
  let high = parts.length - 1;
  let best = 1;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (measure(candidate(mid)) <= maxWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return candidate(best);
}

/**
 * Fits `text` into `maxWidth`: unchanged when it fits, otherwise cut after the
 * last whole word that fits, then "…". When not even the first word fits it
 * is cut by character instead, and never below one character.
 *
 * An unknown width (zero, negative, NaN, Infinity) never cuts. That is what a
 * hidden or not-yet-laid-out element reports, and what jsdom always reports.
 */
export function fitLabel(
  text: string,
  maxWidth: number | undefined,
  measure: MeasureText
): FittedLabel {
  const trimmed = text.trim();
  if (
    maxWidth === undefined ||
    !Number.isFinite(maxWidth) ||
    maxWidth <= 0 ||
    measure(trimmed) <= maxWidth
  ) {
    return { text, cut: false };
  }

  const wordEnds = Array.from(
    trimmed.matchAll(WORD_END),
    (match) => match.index + 1
  );
  for (let index = wordEnds.length - 1; index >= 0; index -= 1) {
    const prefix = stripTrailingJoiners(trimmed.slice(0, wordEnds[index]));
    if (!prefix) continue;
    const candidate = prefix + ELLIPSIS;
    if (measure(candidate) <= maxWidth) return { text: candidate, cut: true };
  }

  // A single character plus "…" is wider than the character alone.
  if (graphemes(trimmed).length <= 1) return { text, cut: false };
  return { text: characterCut(trimmed, maxWidth, measure), cut: true };
}

/** Width left for one item in a flex row once its siblings and gaps are paid. */
export function freeWidthInRow(
  rowWidth: number,
  gap: number,
  otherWidths: readonly number[]
): number {
  const taken = otherWidths.reduce((sum, width) => sum + width, 0);
  return Math.max(0, rowWidth - taken - gap * otherWidths.length);
}
